import { PROCESS_TOOL_NAMES, TASK_TOOL_NAMES } from "../product"
import type { SavedAction, SavedActionTemplate } from "./actionTypes"

const REVIEW_SKILL_URL =
    "https://raw.githubusercontent.com/cursor/plugins/6e3d2ea56d7d446b955eaae6ac4c8eef8bf504cf/cursor-team-kit/skills/thermo-nuclear-code-quality-review/SKILL.md"

function actionPrompt(parts: TemplateStringsArray, ...values: string[]): string {
    const source = parts.reduce((prompt, part, index) => prompt + (index > 0 ? (values[index - 1] ?? "") : "") + part, "")
    return source
        .trim()
        .split(/\n\s*\n/)
        .map((block) => {
            const lines = block
                .split("\n")
                .map((line) => line.trim())
                .filter(Boolean)
            if (!lines.some((line) => line.startsWith("- "))) return lines.join(" ")

            const items: string[] = []
            for (const line of lines) {
                if (line.startsWith("- ") || items.length === 0) {
                    items.push(line)
                    continue
                }
                const previous = items.pop()
                items.push(`${previous ?? ""} ${line}`.trim())
            }
            return items.join("\n")
        })
        .join("\n\n")
}

const CLEAN_CHILD_CONTEXT = actionPrompt`
    Keep the child agent's context clean. Do not include the transcript, the
    current agent's prior analysis or conclusions, suspected findings, preferred
    answers, rejected approaches, examples of unwanted behavior, or instructions
    about what not to do. These details anchor the agent even when negated. Let it
    investigate the sources and reach its own conclusions.
`

const CHILD_OUTPUT_WAIT_POLICY = actionPrompt`
    Thorough work can take 30 minutes or more; elapsed time alone is not evidence
    of a stall. Wait for completed reports before forming the final assessment.
    Use ${TASK_TOOL_NAMES.readSession} to inspect progress when useful to diagnose
    a problem, while preserving each child's independent judgment.
`

// These prompts drive sensitive production workflows. Change them only in small, deliberate increments after a human reviews explicit variants; never wholesale-rewrite them in an agent-authored change.
const DEFAULT_ACTION_TEMPLATE_CATALOG: readonly SavedActionTemplate[] = [
    {
        templateId: "lets-do-it",
        templateVersion: 5,
        enabledByDefault: true,
        insertBeforeTemplateId: "brief",
        action: {
            name: "Go",
            prompt: actionPrompt`
                Carry out the approach we just discussed.

                Use a Git worktree when appropriate. If you create or select a task
                worktree, call ${TASK_TOOL_NAMES.setWorkingDirectory} with its absolute
                root as soon as it is ready so this and subsequent task work happen there.

                Complete the work, verify the result, and report the outcome.
            `,
            icon: "hammer",
            color: "gold",
            requiresConfirmation: false,
            allowCombining: true,
            visibility: {
                match: "all",
                conditions: [],
                onMismatch: "disable",
            },
        },
    },
    {
        templateId: "worktree",
        templateVersion: 1,
        enabledByDefault: true,
        insertBeforeTemplateId: "council",
        action: {
            name: "Worktree",
            prompt: actionPrompt`
                Move all task-owned repository work to a task-specific Git worktree. Use the
                designated task worktree when one is available.

                Inspect the repository, worktrees, branches, current task working directory,
                and staged, unstaged, and untracked changes first. Reuse the existing task
                worktree when it already contains all task-owned work; do not create a
                duplicate worktree or transfer the same work twice.

                Otherwise create the task worktree and transfer every task-owned commit and
                staged, unstaged, and untracked change into it without losing or overwriting
                unrelated work. Stop if ownership or safe transfer is ambiguous. Verify the
                target contains the complete task work before removing transferred source
                copies, and leave unrelated source work untouched.

                After all task-owned work is present in the target worktree, and only then,
                always call ${TASK_TOOL_NAMES.setWorkingDirectory} with its absolute Git root,
                even if the task already points there.

                Do not integrate or push. Report the source, target, and what was transferred
                or already in place.
            `,
            icon: "git-branch",
            color: "zinc",
            requiresConfirmation: false,
            allowCombining: true,
            visibility: {
                match: "all",
                conditions: ["git-repository"],
                onMismatch: "disable",
            },
        },
    },
    {
        templateId: "delegate",
        templateVersion: 7,
        enabledByDefault: true,
        insertBeforeTemplateId: "preview",
        action: {
            name: "Delegate",
            prompt: actionPrompt`
                Delegate this task to the selected agent. Use
                ${TASK_TOOL_NAMES.startSession} in full mode with a self-contained handoff
                covering the objective, requirements, established decisions, verified
                constraints, current state, and relevant source pointers.

                ${CLEAN_CHILD_CONTEXT}

                Give the child ownership of completing and verifying the work.

                Keep calling ${TASK_TOOL_NAMES.waitSession} with long timeouts until the
                child finishes. Allow time for completion; intervene if the child is stuck
                or needs correction.

                ${CHILD_OUTPUT_WAIT_POLICY}

                After the child finishes, call ${TASK_TOOL_NAMES.readSession}, quickly
                review its completed work, and verify that it did the task correctly.

                Return the outcome and any gaps to the user.
            `,
            icon: "fork",
            color: "cobalt",
            requiresConfirmation: false,
            allowCombining: false,
            visibility: {
                match: "all",
                conditions: [],
                onMismatch: "disable",
            },
            sessionLaunch: {
                kind: "delegate",
                description: "Pick an agent to take ownership of completing this task. Your current agent will send it a self-contained handoff.",
            },
        },
    },
    {
        templateId: "independent-review",
        templateVersion: 21,
        enabledByDefault: true,
        insertBeforeTemplateId: "fix-critical",
        action: {
            name: "Review & Clean",
            prompt: actionPrompt`
                Launch exactly two independent child agents per selected target with
                ${TASK_TOOL_NAMES.startSession} in normal full-mode: one Review agent and
                one Clean Junk agent. Use the same selected harness, model, and thinking
                level for both roles. Four selected targets means eight child agents.
                Both roles inspect and report, not edit.

                Reuse the current checkout by default. Create a separate review snapshot
                only when concurrent edits or test isolation require it, and share that
                snapshot across reviewers of the same revision. Give reviewers a descriptive
                session label. For any snapshot created for this task, record its exact
                path, source commit, temporary purpose, and ownership in the handoff so
                later task cleanup can identify it. A borrowed checkout is not task-owned.

                Give each child its assigned role's instructions below and the same factual
                handoff: exact repository, worktree, comparison base, current commit,
                staged, unstaged, and untracked changes, relevant source pointers,
                ${REVIEW_SKILL_URL}, additional instructions, verified constraints, and
                settled product decisions. Include relevant explicit user requests and later
                corrections as verbatim excerpts; label inferred requirements separately.
                These user excerpts belong in the brief; leave the rest of the conversation
                out. Distinguish requirements from implementation choices: the current
                structure is open to challenge. Include the role and model in session labels.

                ${CLEAN_CHILD_CONTEXT}

                Use the same clean handoff for every child. Start every selected target's
                Review and Clean Junk agents before reading any result so their judgments
                remain independent.

                Review agent instructions:

                Use the linked skill as a deep maintainability lens, not as the entire
                review. As relevant to this project, also inspect correctness, regressions,
                missed requirements, security and data safety, state and concurrency
                hazards, failure recovery, compatibility, performance, test coverage, and
                operational risks. This review's evidence and value bar governs the linked
                skill: file-size thresholds, decomposition, or style alone are not blockers.

                Look especially for code-judo opportunities: substantially better
                approaches that remove code, concepts, branches, state, or machinery while
                making the result easier to understand and maintain.

                Judge correctness and urgency separately. A real bug need not warrant
                fixing now. Recommend changes whose practical benefit justifies their
                complexity and cost; defer limitations when that is the better tradeoff.

                Actively try to disprove candidate findings. Trace the reachable trigger
                through callers, boundary guarantees, and owning code; compare with the base
                before calling something a regression. Report supporting and contrary evidence,
                material impact, and what remains unverified. Missing test coverage alone is
                not a demonstrated bug. Hypothetical edge cases alone do not justify defensive
                code, fallback paths, or new tests.

                Recommend the simplest sufficient remedy. Prefer removing an invalid state,
                duplicate path, or unnecessary mechanism over adding special cases. Explain
                why any added state, abstraction, dependency, or operational burden is
                necessary and why a simpler remedy is insufficient. Omit recommendations
                whose benefit does not justify their durable complexity; a review with no
                actionable findings is a valid result.

                Treat explicit decisions and accepted tradeoffs as constraints. The
                reviewer is an adviser, not the authority. If concrete new evidence shows
                that an established decision is unsafe, incorrect, or based on a broken
                assumption, report that under Decisions and risks instead of presenting its
                reversal as a routine fix.

                Report only:

                - Necessary fixes: confirmed defects or hazards with a concrete reason to
                  fix now rather than document and defer.
                - Worthwhile improvements: clear material net wins whose payoff remains
                  compelling after considering their full lifecycle cost.
                - Decisions and risks: credible concerns that require product judgment,
                  significant scope, or reconsidering an established decision.

                Omit nits, speculative concerns, and merely different preferences. Do not
                edit.

                Clean Junk agent instructions:

                Compare the implementation against those requests. Suggest removing added
                behavior, options, abstractions, fallback paths, and tests that are neither
                explicitly requested nor necessary to meet the requirements or established
                obligations. Explain why each is surplus. Preserve necessary supporting
                work even when the user did not spell out its implementation.

                Rethink the implementation from the required behavior.
                Look for substantial reductions in concepts, state, branches, duplication,
                indirection, and unnecessary work. Trace callers and existing guarantees;
                seek a simpler ownership boundary or state model that removes whole paths.
                Consider restructuring beyond the diff when it yields a clear net reduction
                in complexity. Use ${REVIEW_SKILL_URL} for structural inspiration, with this
                brief's net-simplification bar taking precedence over its file-size and
                decomposition rules.

                Preserve required behavior, compatibility, and data safety. Favor direct
                flows and existing primitives. Look for hand-rolled implementations that a
                popular, well-trusted library could replace. Recommend a library only when
                it fits the requirements and reduces net complexity after dependency,
                migration, security, performance, bundle size, verification, and maintenance
                costs. Moving complexity into more files or adding a generic framework is
                not a reduction. Justify performance claims with measured evidence or
                concrete work eliminated.

                Return only high-confidence, material suggestions, ranked by payoff. For
                each, show source locations, a concrete before-and-after approach, what
                code or concepts disappear, any new complexity, preserved requirements,
                and focused validation. Separate changes requiring a product or scope
                decision. If there is no worthwhile simplification, say so; do not force
                a rewrite or fill the report with cosmetic nits.

                Lead coordination and assessment:

                Keep calling ${TASK_TOOL_NAMES.waitSession} with every child concurrently
                and long timeouts until both roles for all selected targets finish. Allow
                time for thorough responses; intervene when there is a concrete problem.

                ${CHILD_OUTPUT_WAIT_POLICY}

                Then call ${TASK_TOOL_NAMES.readSession} for each. As lead, independently
                validate each material finding against the sources and user requirements
                before presenting your assessment. Reviewer confidence or agreement is not
                proof; neither defend the implementation nor accept criticism by default.
                For one or multiple reviewers, report the findings that survive this check
                with your evidence and priority judgment. Summarize material agreements and
                disagreements with attribution, explaining material deferrals when useful.
                Keep unresolved claims explicitly unverified, not Necessary fixes. Do not
                implement the report.

                Keep Review findings and Clean Junk simplification proposals identifiable
                in the combined report, with role and model attribution. For cleanup
                proposals, check the recommendations against the actual requirements and
                code and present only worthwhile proposals. Report requested, completed,
                and failed agent counts plus setup errors; missing reports leave the
                assessment incomplete.
            `,
            icon: "shield",
            color: "fuchsia",
            requiresConfirmation: false,
            allowCombining: false,
            visibility: {
                match: "all",
                conditions: ["git-repository"],
                onMismatch: "disable",
            },
            sessionLaunch: {
                kind: "review",
                description:
                    "Each selected model runs two independent agents: one reviews the work and one proposes simplifications. Both report without editing.",
                maxTargets: 4,
            },
        },
    },
    {
        templateId: "fix-critical",
        templateVersion: 14,
        enabledByDefault: true,
        insertBeforeTemplateId: "start-server",
        action: {
            name: "Fix",
            prompt: actionPrompt`
                Use the latest completed Review & Clean report in this task unless the user
                selects other reports or specific findings. Older standalone Review or
                Clean Junk reports remain valid inputs.

                For the Review findings, by default apply only independently confirmed Necessary fixes
                whose material impact justifies the engineering effort. Leave Worthwhile
                improvements, optional cleanup, and redesigns as suggestions unless the user
                explicitly requested those changes.

                For the Clean Junk proposals, apply independently confirmed material simplifications that
                reduce net complexity while preserving required behavior, compatibility,
                and data safety. Fix following Review & Clean or Clean Junk authorizes those simplifications;
                product or scope decisions still require user approval.

                Independently verify each finding against the current code, requirements,
                repository guidance, and established decisions. The reviewer is an adviser,
                not the authority. Use prior evidence where it remains valid, and investigate
                unresolved claims or changes in the code.

                Be critical even of confirmed bugs and findings labeled Necessary fixes.
                A real bug or reachable edge case is not automatically worth fixing. Weigh
                practical likelihood, user impact, existing workarounds, and regression risk
                against implementation and ongoing maintenance cost. When fixing a low-impact
                limitation would cost more than it helps, document it and defer the work.

                Do not implement Decisions and risks or reverse an explicit decision
                without user approval. Skip findings that are stale, incorrect, speculative,
                marginal, or no longer worth their full lifecycle cost.

                Prefer the smallest coherent solution, especially one that deletes code or
                complexity. Do not introduce disproportionate durable scope, state,
                boundaries, dependencies, migrations, operational burden, or platform
                obligations when a simpler remedy provides the important benefit. Solve
                confirmed root causes rather than accumulating special cases; explain any
                necessary increase in complexity.

                If nothing qualifies, make no code changes. Complete and verify the accepted
                changes. Report requested, applied, skipped, and blocked counts, with
                concise reasons for skipped or blocked findings. Count deliberately deferred
                findings as skipped; explain the documented limitation and what evidence
                or impact would justify revisiting it.
            `,
            icon: "sparkles",
            color: "aqua",
            requiresConfirmation: false,
            allowCombining: true,
            visibility: {
                match: "all",
                conditions: ["git-repository"],
                onMismatch: "disable",
            },
        },
    },
    {
        templateId: "council",
        templateVersion: 19,
        enabledByDefault: true,
        insertBeforeTemplateId: "delegate",
        action: {
            name: "Council",
            prompt: actionPrompt`
                Pressure-test your thinking with independent counsel. Use
                ${TASK_TOOL_NAMES.startSession} in normal full-mode once per selected target.
                The counsel are advisers: ask them to inspect and report, not edit.

                Give the question, requirements, verified constraints, uncertainties, and
                sources.

                ${CLEAN_CHILD_CONTEXT}

                Use the same clean handoff for every counsel. Start every selected counsel
                before reading any result so their judgments remain independent.

                Ask for options, tradeoffs, risks, a recommendation, and contrary evidence.
                Ask the agent to keep it brief, favor simple, elegant systems with fewer,
                clearer components, and surface opportunities to simplify across the system.

                Keep calling ${TASK_TOOL_NAMES.waitSession} with every child and long
                timeouts as needed until all selected turns finish. Allow time for thorough
                responses; intervene when there is a concrete problem.

                ${CHILD_OUTPUT_WAIT_POLICY}

                Then call ${TASK_TOOL_NAMES.readSession} for each and integrate the advice
                you judge worthwhile using established context. With multiple counsel,
                surface material consensus and disagreement and preserve attribution for
                differing recommendations.
            `,
            icon: "idea",
            color: "purple",
            requiresConfirmation: false,
            allowCombining: false,
            visibility: {
                match: "all",
                conditions: [],
                onMismatch: "disable",
            },
            sessionLaunch: {
                kind: "council",
                description: "Select one or more independent counsel for separate recommendations from the same clean brief.",
                maxTargets: 4,
            },
        },
    },
    {
        templateId: "preview",
        templateVersion: 10,
        enabledByDefault: true,
        insertBeforeTemplateId: "independent-review",
        action: {
            name: "Preview",
            prompt: actionPrompt`
                Preview this task by checking out its task branch in the repository's primary
                checkout. Inspect Git and worktree state and require a clean primary
                checkout.

                Commit all task-owned staged, unstaged, and untracked changes in the task
                worktree, then rebase its branch onto the current primary branch when
                needed. Resolve only mechanically obvious conflicts; stop for non-obvious
                conflicts, overlapping work, or ambiguous ownership.

                Detach the clean task worktree at the rebased HEAD, check out the task branch
                in primary, and verify the checkout. Keep the task worktree, branch, and
                commits intact.

                Always call ${TASK_TOOL_NAMES.setWorkingDirectory} with primary's absolute
                root after the verified checkout, even if it is already displayed, and
                continue subsequent task edits there.

                Do not push, integrate, or remove the task worktree. Report the commit,
                rebase and conflict handling, source worktree, and primary checkout.
            `,
            icon: "play",
            color: "violet",
            requiresConfirmation: false,
            allowCombining: true,
            visibility: {
                match: "all",
                conditions: ["git-repository"],
                onMismatch: "disable",
            },
        },
    },
    {
        templateId: "start-server",
        templateVersion: 6,
        enabledByDefault: true,
        insertBeforeTemplateId: "commit",
        action: {
            name: "Local Server",
            prompt: actionPrompt`
                Prepare and start this project from the current task checkout, keeping the
                task in its assigned worktree when it has one.

                Stay in this checkout; do not move the task to the primary checkout or
                commit, rebase, push, integrate, or clean up its worktree.

                Inspect the repository's instructions, lockfiles, scripts, environment
                configuration, and process definitions, then use its declared package
                manager and runtime. Reuse an existing worktree-safe setup and launch path
                when present.

                Make any setup changes needed to run this checkout, using available ports
                and preserving other running services.

                Reuse the same configured database and shared backing services without
                copying secrets into tracked files or creating a separate datastore.

                Install or bootstrap only what is needed, start the server through the
                environment's persistent process facility, verify readiness at the actual
                URL, and report the URL, process and log details, selected ports, shared
                services, and any one-time files changed.
            `,
            icon: "terminal",
            color: "azure",
            requiresConfirmation: false,
            allowCombining: true,
            visibility: {
                match: "all",
                conditions: ["git-repository"],
                onMismatch: "disable",
            },
        },
    },
    {
        templateId: "commit",
        templateVersion: 9,
        enabledByDefault: true,
        insertBeforeTemplateId: "land",
        action: {
            name: "Commit",
            prompt: actionPrompt`
                Commit the task's current changes on the current branch.

                Do not push, integrate, or clean up the worktree.
            `,
            icon: "commit",
            color: "slate",
            requiresConfirmation: false,
            allowCombining: true,
            visibility: {
                match: "all",
                conditions: ["git-repository"],
                onMismatch: "disable",
            },
        },
    },
    {
        templateId: "land",
        templateVersion: 8,
        enabledByDefault: true,
        insertBeforeTemplateId: "push",
        action: {
            name: "Land",
            prompt: actionPrompt`
                Integrate the committed task branch through the documented workflow and
                verify the result, reusing checks that remain valid for the integrated tree.

                Under direct integration, preserve clean commits already ahead on the local
                default branch; if it is clean and divergent from its upstream, rebase its
                local-only commits onto the fetched upstream before integrating.

                Honor explicit local-only or no-push instructions.

                Resolve obvious conflicts; stop for unresolved review, approval, non-obvious
                conflicts, or a dirty default checkout.

                Do not commit task changes or clean up its worktree.
            `,
            icon: "merge",
            color: "sky",
            requiresConfirmation: false,
            allowCombining: true,
            visibility: {
                match: "all",
                conditions: ["git-repository"],
                onMismatch: "disable",
            },
        },
    },
    {
        templateId: "pull-request",
        templateVersion: 22,
        enabledByDefault: true,
        insertBeforeTemplateId: "deploy",
        action: {
            name: "PR",
            prompt: actionPrompt`
                Create or update a pull or merge request from the current branch to the
                repository's normal integration branch.

                Use the GitHub or GitLab CLI when appropriate. Push the branch as needed.

                Do not commit task changes or merge the request.

                If task changes are uncommitted, stop and report the required Commit step.

                Whether you create, update, or find an existing request, call
                ${TASK_TOOL_NAMES.linkPR} with its canonical URL and provider node ID
                when available before finishing. The PR step is incomplete until that callback
                succeeds: it attaches the request to this task and makes PR automation controls available.
                If linking fails, report that partial result instead of claiming the PR step is complete.
            `,
            icon: "pull-request",
            color: "indigo",
            requiresConfirmation: false,
            allowCombining: true,
            visibility: {
                match: "all",
                conditions: ["git-repository"],
                onMismatch: "disable",
            },
        },
    },
    {
        templateId: "wait-on-pr",
        templateVersion: 3,
        enabledByDefault: true,
        insertBeforeTemplateId: "deploy",
        action: {
            name: "Wait on PR",
            prompt: actionPrompt`
                Put this task to sleep until its linked GitHub pull request is ready to land.

                Read the linked request with ${TASK_TOOL_NAMES.readPR}. If no supported open
                request is linked, report the blocker and stop.

                If Watch PR is off, enable it with ${TASK_TOOL_NAMES.setPRAutomation}.
                Preserve an existing watch. If setup fails, report the errors and stop.

                Call ${TASK_TOOL_NAMES.sleepTask} with until="pr_ready". The watcher monitors
                approvals and checks, repairs blockers, and wakes the task when ready or
                when human attention is needed. If that condition already holds, sleep
                returns the task awake with the reason.

                Report the returned state and any setup errors, then finish this turn.
                Do not poll, schedule follow-ups, use a time-based or indefinite sleep,
                merge, or close.
            `,
            icon: "timer",
            color: "lavender",
            requiresConfirmation: false,
            allowCombining: true,
            visibility: {
                match: "all",
                conditions: ["git-repository"],
                onMismatch: "disable",
            },
        },
    },
    {
        templateId: "push",
        templateVersion: 12,
        enabledByDefault: true,
        enableOnUpgradeThroughVersion: 9,
        insertBeforeTemplateId: "pull-request",
        action: {
            name: "Push",
            prompt: actionPrompt`
                Fetch remote state, then publish every relevant local branch: the current
                non-default task branch when it has unpublished work or an existing
                upstream, and the local default branch when it is clean and ahead-only of
                its upstream.

                If the task branch is fully contained in the default branch and has no
                upstream, do not create a remote branch for it.

                Stop if the remote or branch target is ambiguous, history has diverged, or
                any push would be non-fast-forward.

                Do not commit, merge, rebase, force-push, stash, discard changes, or clean
                up worktrees.

                Report requested/succeeded/failed branch counts, each outcome, and the first
                errors.
            `,
            icon: "push",
            color: "blue",
            requiresConfirmation: false,
            allowCombining: true,
            visibility: {
                match: "all",
                conditions: ["git-repository"],
                onMismatch: "disable",
            },
        },
    },
    {
        templateId: "deploy",
        templateVersion: 3,
        enabledByDefault: true,
        insertBeforeTemplateId: "check-back",
        action: {
            name: "Deploy",
            prompt: actionPrompt`
                Deploy this task from the current checkout using the repository's documented
                deployment workflow.

                Determine the exact target and environment from explicit task context and
                repository configuration; if either is ambiguous, would create a new
                environment, or requires credentials or approval not already available,
                stop and ask rather than guessing.

                Run the workflow's required checks and build, preserve its existing
                credentials and environment configuration, and do not commit, integrate, or
                clean up the worktree.

                Perform the deployment once, verify the deployed revision and health at the
                real endpoint, and report the target, revision, URL, checks, and any gaps.
            `,
            icon: "rocket",
            color: "coral",
            requiresConfirmation: false,
            allowCombining: true,
            visibility: {
                match: "all",
                conditions: ["git-repository"],
                onMismatch: "disable",
            },
        },
    },
    {
        templateId: "check-back",
        templateVersion: 10,
        enabledByDefault: true,
        insertBeforeTemplateId: "close",
        action: {
            name: "Follow",
            prompt: actionPrompt`
                Follow through on this task.

                Use known task timing to choose when a meaningful new signal is likely;
                investigate cadence if needed.

                Use ${TASK_TOOL_NAMES.scheduleFollowup} to schedule one check no earlier than
                that point. If timing is unclear, choose a conservative
                interval and state the assumption.

                On each check, act on findings, then schedule one evidence-timed successor
                only if another concrete signal is expected, or end the series when stable.
            `,
            icon: "timer",
            color: "amber",
            requiresConfirmation: false,
            allowCombining: true,
            visibility: {
                match: "all",
                conditions: [],
                onMismatch: "disable",
            },
        },
    },
    {
        templateId: "brief",
        templateVersion: 7,
        enabledByDefault: true,
        insertBeforeTemplateId: "worktree",
        action: {
            name: "Brief",
            prompt: actionPrompt`
                Give me a concise, self-contained brief of this task and the relevant product
                or system.

                Include the product and technical context that helps someone understand or
                continue this task. Explain important data flows across domains and systems:
                where data enters, how it changes, where it is stored, and where it goes.

                Summarize what is done, the current state, what remains, any blockers or
                important unknowns, and the natural next step. Distinguish confirmed facts
                from assumptions. Include only details that matter to understanding or
                continuing this task, and omit empty topics.
            `,
            icon: "checklist",
            color: "cyan",
            requiresConfirmation: false,
            allowCombining: true,
            visibility: {
                match: "all",
                conditions: [],
                onMismatch: "disable",
            },
        },
    },
    {
        templateId: "close",
        templateVersion: 28,
        enabledByDefault: true,
        insertBeforeTemplateId: "abandon",
        action: {
            name: "Close Out",
            prompt: actionPrompt`
                Make sure our intended work is complete, safely preserved, and ready to
                close out. For work outside Git, preserve the completed files in place.

                For repository work, close this task only if all intended changes are integrated into the local
                default branch. A squash merge or other history rewrite counts when it
                carries equivalent content; do not require the original task commit SHAs to
                be present in or ancestors of the default branch. If history does not prove
                integration, compare the task changes with the local default branch and stop
                if any material intended change is missing. Its remote may lag or be absent.

                In each involved repository, check staged, unstaged, and untracked task-owned changes.
                If any intended work is uncommitted or unintegrated, leave the task open.

                Use ${TASK_TOOL_NAMES.listSessions} and ${TASK_TOOL_NAMES.listFollowups}
                as needed to establish completion and safe cleanup.

                Stop task-owned managed processes that are no longer needed, using
                ${PROCESS_TOOL_NAMES.list} and ${PROCESS_TOOL_NAMES.stop}. Preserve shared
                processes and report any cleanup problems.

                Prefer removing task-owned worktrees, including temporary review snapshots,
                when safe to reclaim disk space. Use available task context to establish
                ownership; retain a checkout if ownership is uncertain.
                Never remove primary or borrowed checkouts.
                Preserve checkouts needed by other live sessions or pending
                follow-ups, branches, and commits. Detached HEAD alone does not prove a
                checkout is disposable; its commit must remain reachable from a retained ref.

                Report process and worktree cleanup requested/succeeded/failed counts,
                first errors, and retained paths with reasons. Report cleanup gaps without
                blocking closure unless they put work at risk. Once intended work is complete,
                integrated where applicable, and safely preserved, call ${TASK_TOOL_NAMES.closeTask}.

                Do not commit, integrate, or push.
            `,
            icon: "wrap-up",
            color: "orange",
            requiresConfirmation: false,
            allowCombining: true,
            visibility: {
                match: "all",
                conditions: [],
                onMismatch: "disable",
            },
        },
    },
    {
        templateId: "abandon",
        templateVersion: 10,
        enabledByDefault: true,
        enableOnUpgradeThroughVersion: 7,
        action: {
            name: "Abandon",
            prompt: actionPrompt`
                Abandon this task without integrating or pushing it.

                Call ${TASK_TOOL_NAMES.listSessions} and ${TASK_TOOL_NAMES.listFollowups}. Verify ownership from
                creation/handoff records, not session association or detached HEAD.
                Preserve uncommitted task-owned
                changes in commits on non-default branches, including a recovery branch for
                any detached checkout with unique work; stop if ownership or preservation
                is unclear.

                Clean up every verified task-owned worktree, including temporary review
                snapshots, while keeping branches, commits, borrowed checkouts, and checkouts
                needed by other live sessions or pending follow-ups. Report requested/succeeded/failed
                counts, first errors, and retained paths with reasons. Cleanup failures are
                done-with-gaps. Leave the task open on cleanup failures; otherwise call
                ${TASK_TOOL_NAMES.closeTask}.

                Never remove the primary checkout or commit on the default branch.
            `,
            icon: "trash",
            color: "rose",
            requiresConfirmation: true,
            allowCombining: true,
            visibility: {
                match: "all",
                conditions: [],
                onMismatch: "disable",
            },
        },
    },
] as const

const DEFAULT_ACTION_TEMPLATE_ORDER = [
    "lets-do-it",
    "brief",
    "worktree",
    "council",
    "delegate",
    "preview",
    "independent-review",
    "fix-critical",
    "start-server",
    "commit",
    "land",
    "push",
    "pull-request",
    "wait-on-pr",
    "deploy",
    "check-back",
    "close",
    "abandon",
] as const

function orderDefaultActionTemplates(templates: readonly SavedActionTemplate[], templateIds: readonly string[]): readonly SavedActionTemplate[] {
    const templatesById = new Map(templates.map((template) => [template.templateId, template]))
    if (templatesById.size !== templates.length || templateIds.length !== templates.length || new Set(templateIds).size !== templateIds.length) {
        throw new Error("Default Action catalog and order must contain the same unique templates")
    }

    return templateIds.map((templateId) => {
        const template = templatesById.get(templateId)
        if (!template) throw new Error(`Default Action order references unknown template ${templateId}`)
        return template
    })
}

export const DEFAULT_ACTION_TEMPLATES = orderDefaultActionTemplates(DEFAULT_ACTION_TEMPLATE_CATALOG, DEFAULT_ACTION_TEMPLATE_ORDER)

export function createInstalledDefaultAction(template: SavedActionTemplate, installedAt: string): SavedAction {
    return {
        id: `default.${template.templateId}`,
        ...globalThis.structuredClone(template.action),
        enabled: template.enabledByDefault,
        source: {
            templateId: template.templateId,
            templateVersion: template.templateVersion,
        },
        createdAt: installedAt,
        updatedAt: installedAt,
    }
}

export function createInstalledDefaultActions(now = new Date().toISOString()): SavedAction[] {
    return DEFAULT_ACTION_TEMPLATES.map((template) => createInstalledDefaultAction(template, now))
}

export function defaultActionTemplate(templateId: string): SavedActionTemplate | undefined {
    return DEFAULT_ACTION_TEMPLATES.find((template) => template.templateId === templateId)
}
